import React from "react";
import EcoIcon from "./ecoIcon";

// Rain Room Creative green — used (faded) for the Design & Dev card so it blends
// with the navy/graphite palette instead of shouting.
export const RRC_GREEN = "#3DAE7A";

// Keyframes + hover triggers for the animated overview-card icons.
// Rendered once by ServiceCards. All motion is slow + smooth, and disabled
// under prefers-reduced-motion. Triggered on card hover (each card is a `group`).
export const ServiceCardIconStyles = () => (
    <style>{`
        .sc-box {
            background: var(--eco-bg);
            border-color: var(--eco-line);
            transition: background-color 1100ms ease, border-color 1100ms ease;
        }

        /* ---- 01 · PM: a task list that settles, then checks off line by line ---- */
        .sc-pm-list { display: flex; flex-direction: column; gap: 4px; }
        .group:hover .sc-pm-list { animation: scPmSettle 900ms cubic-bezier(.2,.7,.2,1) both; }
        @keyframes scPmSettle { from { transform: translateY(4px); opacity: .55; } to { transform: translateY(0); opacity: 1; } }
        .sc-pm-check { opacity: 0; transform: scale(.2); transform-origin: center;
            transition: opacity 380ms ease, transform 380ms cubic-bezier(.2,1.3,.4,1); }
        .group:hover .sc-pm-check { opacity: 1; transform: scale(1); }
        .group:hover .sc-pm-check.d1 { transition-delay: 800ms; }
        .group:hover .sc-pm-check.d2 { transition-delay: 1050ms; }
        .group:hover .sc-pm-check.d3 { transition-delay: 1300ms; }

        /* ---- 02 · Ops: the sun turns slowly, like a steady operating rhythm ---- */
        .sc-sun { transform-origin: center; }
        .group:hover .sc-sun { animation: scSpin 22s linear infinite; }

        /* ---- 03 · Design: the diamond turns; the tile warms to a faded RRC green ---- */
        .sc-diamond { transform-origin: center; }
        .group:hover .sc-diamond { animation: scSpin 16s linear infinite; }
        .group:hover .sc-box-dd { background-color: rgba(61,174,122,0.16); border-color: rgba(61,174,122,0.45); }

        @keyframes scSpin { to { transform: rotate(360deg); } }

        @media (prefers-reduced-motion: reduce) {
            .sc-pm-list, .sc-sun, .sc-diamond { animation: none !important; transform: none !important; }
            .sc-pm-check { opacity: 1 !important; transform: none !important; }
            .sc-box { transition: none !important; }
        }
    `}</style>
);

// A small green check, drawn to the right of each PM list row.
const MiniCheck = ({ delayClass }) => (
    <svg className={`sc-pm-check ${delayClass}`} width="7" height="7" viewBox="0 0 24 24" fill="none">
        <path d="M5 12l5 5L20 7" stroke={RRC_GREEN} strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// Kept narrow on purpose so the whole list sits centered inside the 48px tile.
const PmIcon = ({ tint }) => {
    const rows = [
        { w: 16, d: "d1" },
        { w: 13, d: "d2" },
        { w: 10, d: "d3" },
    ];
    return (
        <div className="sc-pm-list">
            {rows.map((r) => (
                <div key={r.d} className="flex items-center gap-1">
                    <span
                        className="rounded-full"
                        style={{ width: r.w, height: 2.5, background: tint.ink, opacity: 0.85 }}
                    />
                    <MiniCheck delayClass={r.d} />
                </div>
            ))}
        </div>
    );
};

const OpsIcon = ({ tint }) => (
    <span className="sc-sun grid place-items-center">
        <EcoIcon name="ops" size={24} color={tint.ink} />
    </span>
);

const DesignIcon = ({ tint }) => (
    <span className="sc-diamond grid place-items-center">
        <EcoIcon name="design" size={22} color={tint.ink} />
    </span>
);

const RENDERERS = { fpm: PmIcon, fops: OpsIcon, fdd: DesignIcon };

// The full tinted icon tile for an overview card. Replaces the static icon span.
const ServiceCardIcon = ({ service, tint }) => {
    const Inner = RENDERERS[service.id] || OpsIcon;
    const boxClass = "sc-box w-12 h-12 rounded-xl grid place-items-center border border-solid" +
        (service.id === "fdd" ? " sc-box-dd" : "");
    return (
        <span className={boxClass} style={{ "--eco-bg": tint.bg, "--eco-line": tint.line }}>
            <Inner tint={tint} />
        </span>
    );
};

export default ServiceCardIcon;
